import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Paper from "@mui/material/Paper";
import {createUUId} from "../../../../../core/services/utils/utils";
import {OrderDimensionsSectionContainerInfoManagement} from "./container-info-management";
import {OrderDimensionsSectionPartsManagement} from "./parts-management";
import {Box, Typography} from "@mui/material";

import {DimensionMetricController} from "../../manage-product-dimensions/unit-management";
import Api from "../../../../../core/services/api/api";
import {buildMergedOrderParts} from "./dimensions-utils";


const containerTypes = {
    pallet: "Pallet",
    package: "Package",
}

const OrderDimensionsSection = ({
                                    orderId,
                                    invoiceId,
                                    items,
                                    loading: loadingOrder,
                                }) => {
    const [data, setData] = useState({});
    const [loadingData, setLoadingData] = useState(true);
    const [partNoIdMap, setPartNoIdMap] = useState({});
    const isMounted = useIsMounted();

    /**
     * Fetch the order part dimensions
     * @type {(function(*): Promise<void>)}
     */
    const getOrderPartDimensionInformation = useCallback(async () => {
        setLoadingData(true);
        const [containersResponse, partsResponse] = await Promise.all([
            Api.getOrderPalletsList({orderId: orderId, invoiceId: invoiceId,}, undefined, false),
            Api.getOrderDimensionsList({orderId: orderId, invoiceId: invoiceId,}, undefined, false),
        ]);
        if (!isMounted())
            return;
        if (containersResponse?.resultFlag) {
            const containers = containersResponse.data ?? [];
            const containerType = containers?.find(e => !!e.type)?.type;
            setData((prev) => ({
                ...prev,
                containerType: containerType || containerTypes.pallet,
                containers: containers?.map(container => ({...container, uniqueId: createUUId(true)})) ?? [],
            }))
        } else {
            // in case we get an error
            setData((prev) => ({...prev, containerType: containerTypes.pallet, containers: []}))
        }
        if (partsResponse?.resultFlag) {
            const orderDimensions = partsResponse.data ?? [];
            setData((prev) => ({
                ...prev,
                orderDimensions: orderDimensions ?? [],
            }))
        } else {
            // in case we get an error
            setData((prev) => ({...prev, orderDimensions: [],}))
        }
        setLoadingData(false);
    }, [invoiceId, isMounted, orderId]);

    /**
     * Fetch the order part dimensions when the orderNo changes
     */
    useEffect(() => {
        void getOrderPartDimensionInformation();
    }, [getOrderPartDimensionInformation])

    const partNos = useMemo(() => {
        const res = new Set();
        for (const orderPart of items ?? []) {
            res.add(orderPart.partNo);
        }
        return Array.from(res);
    }, [items]);

    const partNoStringified = useMemo(() => [...partNos].sort().join('___'), [partNos]);

    /**
     * Get part IDs from part Nos
     * @type {(function(*): Promise<void>)|*}
     */
    const getPartIdsFromPartNos = useCallback(async (partNos) => {
        const response = await Api.getPartInfoWithPartNumber({partNumbers: partNos});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const data = response.data ?? [];
            // Prefill missing part Nos with null IDs
            for (const partNo in partNos) {
                if (!data.find(partInfo => partInfo.partNo === partNos[partNo])) {
                    data.push({partNo: partNos[partNo], id: null});
                }
            }
            setPartNoIdMap(prev => {
                const res = {...prev};
                for (const partInfo of data) {
                    res[partInfo.partNo] = partInfo.id;
                }
                return res;
            });
        }
    }, [isMounted]);

    /**
     * Get part IDs from part Nos
     */
    useEffect(() => {
        const partNos = partNoStringified.split('___').filter(e => !!e);
        const missingPartIds = partNos.filter(partNo => partNoIdMap[partNo] === undefined);
        if (missingPartIds.length === 0)
            return;
        void getPartIdsFromPartNos(missingPartIds);
    }, [getPartIdsFromPartNos, partNoIdMap, partNoStringified]);

    const mergedParts = useMemo(
        () => buildMergedOrderParts(items, data.orderDimensions, partNoIdMap),
        [items, data.orderDimensions, partNoIdMap],
    );

    /**
     * Set the containers
     * @type {(function(*): void)}
     */
    const setContainerType = useCallback((updater) => {
        setData(prevData => {
            const newContainerType = typeof updater === 'function' ? updater(prevData.containerType) : updater;
            return {
                ...prevData,
                containerType: newContainerType,
            };
        });
    }, []);

    /**
     * Set the containers
     * @type {(function(*): void)}
     */
    const setContainers = useCallback((updater) => {
        setData(prevData => {
            const newContainers = typeof updater === 'function' ? updater(prevData.containers) : updater;
            return {
                ...prevData,
                containers: newContainers ?? [],
            };
        });
    }, []);

    /**
     * Set the order dimensions
     * @type {(function(*): void)}
     */
    const setOrderDimensions = useCallback((updater) => {
        setData(prevData => {
            const newRecords = typeof updater === 'function' ? updater(prevData.orderDimensions) : updater;
            return {
                ...prevData,
                orderDimensions: newRecords ?? [],
            };
        });
    }, []);

    /**
     * Replace a container with another container
     * @type {(function(*, *))}
     */
    const replaceContainerWith = useCallback((oldPallet, newPallet) => {
        setData(prevData => {
            return ({
                ...(prevData ?? {}),
                orderDimensions: prevData.orderDimensions?.map(record => ({
                    ...record,
                    orderPalletId: record.orderPalletId === oldPallet.id ? newPallet.id : record.orderPalletId,
                    orderPalletTitle: record.orderPalletId === oldPallet.id ? newPallet.title : record.orderPalletTitle,
                })) ?? [],
            })
        });
    }, [])

    const containerTypeList = useMemo(() => [
        {title: "Package", value: containerTypes.pallet},
        {title: "Pallet", value: containerTypes.package},
    ], []);

    const currentContainerTypeTitle = useMemo(() => containerTypeList.find(type => type.value === data.containerType)?.title, [data.containerType, containerTypeList]);

    const allContainers = useMemo(() => data.containers ?? [], [data.containers]);

    const committedContainers = useMemo(() => data.containers?.filter(container => !!container.id) ?? [], [data.containers]);

    /**
     * Get the container title
     */
    const getContainerTitle = useCallback((container, onlyCommitted = false) => container.title || `${currentContainerTypeTitle} ${(onlyCommitted ? committedContainers : allContainers).findIndex(e => e.uniqueId === container.uniqueId) + 1}`, [currentContainerTypeTitle, committedContainers, allContainers]);

    return (
        <>
            <Paper variant={'outlined'} sx={{display: 'flex', flexDirection: 'column', gap: 3, border: 'none', padding: 3, width: '100%'}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        Product Dimensions
                    </Typography>
                    <DimensionMetricController/>
                </Box>
                <OrderDimensionsSectionContainerInfoManagement
                    containerType={data.containerType}
                    containers={allContainers}
                    mergedParts={mergedParts}
                    setContainerType={setContainerType}
                    setContainers={setContainers}
                    loading={loadingData || loadingOrder}
                    containerTypeList={containerTypeList}
                    currentContainerTypeTitle={currentContainerTypeTitle}
                    getContainerTitle={getContainerTitle}
                    replaceContainerWith={replaceContainerWith}
                    orderId={orderId}
                    invoiceId={invoiceId}
                />
                <OrderDimensionsSectionPartsManagement
                    orderParts={items}
                    mergedParts={mergedParts}
                    containers={committedContainers}
                    setOrderDimensions={setOrderDimensions}
                    loading={loadingData || loadingOrder}
                    currentContainerTypeTitle={currentContainerTypeTitle}
                    getContainerTitle={getContainerTitle}
                    orderId={orderId}
                    invoiceId={invoiceId}
                />
            </Paper>
        </>
    );
}


export default OrderDimensionsSection;

